// Output rendering and status reporting for playground compile results.
// Example: updates generated CSS panel and status labels.
import type { LuisCompileResult, LuisCompileWarning } from '@lentystyle/compiler'
import { formatBytes, formatDuration, getLineCount, getUtf8ByteLength } from './format'
import { setLineNumbers, syncLineNumberScroll } from './line-numbers'
import { playgroundMessages, formatPlaygroundCount } from './messages'
import { elements } from './state'

// Render generated CSS into the single output panel.
export function renderResult(result: LuisCompileResult): void {
  elements.cssOutput.textContent = result.css
  setLineNumbers(elements.cssLineNumbers, result.css)
  syncLineNumberScroll(elements.cssOutput, elements.cssLineNumbers)
  updateCssStats(result.css)
  updateLuisStats(elements.luisInput.value)
}

// Update status line with tone-based styling.
export function setStatus(message: string, tone: 'ok' | 'error'): void {
  elements.status.textContent = message
  elements.status.dataset.tone = tone
}

// Normalize thrown values into a readable status message.
export function handleError(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error)
  console.error(`[LentyStyle] Playground compile error: ${message}`, error)
  setStatus(message, 'error')
}

// Show last compile duration in a compact format.
export function setCompilePerf(durationMs: number): void {
  elements.compilePerf.textContent = `${playgroundMessages.cssGenerationTimeLabel}: ${formatDuration(durationMs)}`
}

// Show generated CSS size metrics directly in the CSS output panel.
export function updateCssStats(css: string): void {
  const lineCount = getLineCount(css)
  const charCount = css.length
  const byteCount = getUtf8ByteLength(css)
  elements.cssStats.textContent =
    `${playgroundMessages.generatedCssLabel}: ${lineCount} ${playgroundMessages.linesLabel}, `
    + `${formatPlaygroundCount(charCount)} ${playgroundMessages.charsLabel}, ${formatBytes(byteCount)}`
}

// Show luis source metrics directly in the input panel.
export function updateLuisStats(source: string): void {
  const lineCount = getLineCount(source)
  const charCount = source.length
  const byteCount = getUtf8ByteLength(source)
  elements.luisStats.textContent =
    `${playgroundMessages.luisSourceLabel}: ${lineCount} ${playgroundMessages.linesLabel}, `
    + `${formatPlaygroundCount(charCount)} ${playgroundMessages.charsLabel}, ${formatBytes(byteCount)}`
}

// Print soft compile warnings to browser console for quick diagnostics.
export function logCompileWarnings(warnings: LuisCompileWarning[]): void {
  if (warnings.length === 0) {
    return
  }

  for (const warning of warnings) {
    console.warn(`[LentyStyle] ${warning.message} (line ${warning.line}, code: ${warning.code}).`)
  }
}
